/**
 * audioLevelStore: Redux 외부에서 오디오 레벨을 관리하는 경량 스토어
 *
 * audioLevels는 ~200ms마다 업데이트되어 Redux 디스패치 시 전체 컴포넌트를 리렌더링시킵니다.
 * 이 스토어는 useSyncExternalStore와 함께 사용하여 특정 userId의 레벨이 변경될 때만
 * 해당 컴포넌트만 리렌더링되도록 합니다.
 */

type Listener = () => void;

class AudioLevelStore {
  private _levels: Record<string, number> = {};
  private _listeners = new Set<Listener>();
  private _snapshot = this._levels;

  setLevel(userId: string, level: number) {
    if (level > 0) {
      if (this._levels[userId] === level) return;
      this._levels[userId] = level;
    } else {
      if (!(userId in this._levels)) return;
      delete this._levels[userId];
    }
    this._snapshot = { ...this._levels };
    this._emit();
  }

  clearLevel(userId: string) {
    if (!(userId in this._levels)) return;
    delete this._levels[userId];
    this._snapshot = { ...this._levels };
    this._emit();
  }

  clearAll() {
    if (Object.keys(this._levels).length === 0) return;
    this._levels = {};
    this._snapshot = this._levels;
    this._emit();
  }

  getLevel(userId: string): number {
    return this._levels[userId] ?? 0;
  }

  getSnapshot(): Record<string, number> {
    return this._snapshot;
  }

  subscribe = (listener: Listener): (() => void) => {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  };

  private _emit() {
    this._listeners.forEach((fn) => {
      try { fn(); } catch { /* listener error should not break caller */ }
    });
  }
}

export const audioLevelStore = new AudioLevelStore();
